import React, { useEffect, useMemo, useState } from 'react';

import UserSearchSelect from '../../components/UserSearchSelect.jsx';
import { COMPLAINT_STATUS, complaintCapabilities } from '../../config';
import { assigneeOrSelf } from '../../lib/assignees';
import { useAuth } from '../../lib/auth.jsx';
import api from '../../lib/api';
import { API } from '../../config';

/**
 * Reusable Complaint form, used by Create / Edit.
 *
 * The Django model `Complaint` has many fields — we expose the most useful
 * ones; the rest can be edited from the Django admin if ever needed.
 *
 * Two audiences, one form
 * -----------------------
 * Internal staff see the full form (division, sub-division, assignee,
 * status, complainant identity).  An Auretics **external** user sees only
 * the three fields they own — title, description, attachment — because
 * ``external_complaint_defaults`` stamps division / sub-division /
 * assignee / ticket number server-side and IGNORES anything the client
 * sends for them.  Rendering those pickers for an external user would
 * therefore be a lie: their choices would be silently overwritten.
 */
export default function ComplaintForm({ initial = {}, onSubmit, submitting, mode = 'create' }) {
  const { user } = useAuth();
  const can = complaintCapabilities(user);
  const selfId = user?.id ?? null;

  const defaultAssignTo = can.isExternal
    ? (initial.assign_to || '')
    : (assigneeOrSelf(initial.assign_to, selfId) ?? '');

  const [form, setForm] = useState({
    complaint_title: '',
    description: '',
    divisions: '',
    sub_divisions: '',
    assign_to: defaultAssignTo,
    status: 'pending',
    fullname: '',
    mobile_number: '',
    email: '',
    registeration_number: '',
    file: null,
    ...initial,
    assign_to: initial.assign_to != null && initial.assign_to !== ''
      ? initial.assign_to
      : defaultAssignTo,
  });
  const [divisions, setDivisions] = useState([]);

  const knownAssignees = useMemo(() => {
    const rows = [];
    if (user) rows.push(user);
    if (initial.assign_to) {
      rows.push({
        id: initial.assign_to,
        email: initial.assign_to_email || '',
        full_name: initial.assign_to_name || '',
      });
    }
    return rows;
  }, [user, initial.assign_to, initial.assign_to_email, initial.assign_to_name]);

  useEffect(() => {
    // External users never see the assignee / division pickers, so skip
    // both lookups entirely.  Besides being wasted round-trips, the user
    // directory and the division list are internal org data an outside
    // complainant has no business enumerating.
    if (!can.canListUsers) return;

    // Divisions endpoint may or may not exist depending on org config — be tolerant.
    api
      .get(API.ENDPOINTS.DIVISIONS, { params: { page_size: 200 } })
      .then((r) => setDivisions(r.data.results || r.data))
      .catch(() => setDivisions([]));
  }, [can.canListUsers]);

  useEffect(() => {
    if (can.isExternal) return;
    if (selfId == null) return;
    setForm((f) => (
      f.assign_to != null && f.assign_to !== ''
        ? f
        : { ...f, assign_to: selfId }
    ));
  }, [selfId, can.isExternal]);


  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();

    const assignTo = can.isExternal
      ? form.assign_to
      : (can.isIndividual
        ? selfId
        : assigneeOrSelf(form.assign_to, selfId));

    // If a file is attached we need multipart/form-data.
    if (form.file) {
      const fd = new FormData();
      Object.entries({ ...form, assign_to: assignTo }).forEach(([k, v]) => {
        if (k === 'assign_to_email' || k === 'assign_to_name') return;
        if (v !== null && v !== undefined && v !== '') fd.append(k, v);
      });
      onSubmit(fd);
    } else {
      const payload = { ...form, assign_to: assignTo };
      delete payload.file;
      delete payload.assign_to_email;
      delete payload.assign_to_name;
      // Strip EVERY empty string rather than naming fields one by one.
      //
      // The old code only deleted ``divisions`` and ``assign_to``, so
      // ``sub_divisions: ''`` (from this component's initial state) was
      // still POSTed.  DRF treats present-but-empty differently from
      // absent: it skips the ``required`` check and fails
      // ``allow_blank`` instead, so an Auretics-portal user submitting
      // the form got
      //
      //     400 {"sub_divisions": ["This field may not be blank."]}
      //
      // even though the server was going to overwrite that field
      // anyway.  Dropping all blanks keeps this branch consistent with
      // the multipart branch above (which already skips ``''``), and
      // means adding a new optional input can't reintroduce the bug.
      //
      // Safe because a blank input carries no information: for optional
      // fields DRF applies its default, and for genuinely required
      // staff-side fields the resulting "this field is required" is the
      // correct, clearer error.  ``assign_to`` is filled above for
      // internal staff, so it survives this strip.
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '') delete payload[k];
      });
      onSubmit(payload);

    }
  };

  return (
    <form onSubmit={handleSubmit} className="ty-card">
      <div className="ty-field">
        <label>Title *</label>
        <input
          className="ty-input"
          required
          value={form.complaint_title}
          onChange={(e) => set('complaint_title', e.target.value)}
        />
      </div>

      <div className="ty-field">
        <label>Description</label>
        <textarea
          className="ty-textarea"
          value={form.description || ''}
          onChange={(e) => set('description', e.target.value)}
        />
      </div>

      {/* Routing + lifecycle fields: internal staff only.  For external
          users the server stamps these from their org mapping.  Individuals
          skip the people/division pickers — assignment is always self. */}
      {!can.isExternal && (
      <div className="ty-grid-2">
        {can.canListUsers && (
        <>
        <div className="ty-field">
          <label>Division</label>

          <select
            className="ty-select"
            value={form.divisions || ''}
            onChange={(e) => set('divisions', e.target.value)}
          >
            <option value="">— Select —</option>
            {divisions.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>

        <div className="ty-field">
          <label>Sub-division</label>
          <input
            className="ty-input"
            value={form.sub_divisions || ''}
            onChange={(e) => set('sub_divisions', e.target.value)}
          />
        </div>

        <div className="ty-field">
          <label htmlFor="complaint-assign-to">Assign to *</label>
          <UserSearchSelect
            id="complaint-assign-to"
            multiple={false}
            value={form.assign_to ? [Number(form.assign_to)] : []}
            onChange={(ids) => set('assign_to', assigneeOrSelf(ids[0], selfId) ?? '')}
            knownUsers={knownAssignees}
            selfId={selfId}
          />
          <small className="ty-muted">
            Defaults to Self. Search by name, mobile or email. Three recent
            assignees appear as suggestions when you open the field.
          </small>
        </div>
        </>
        )}

        {can.isIndividual && (
          <div className="ty-field">
            <label>Assign to</label>
            <input className="ty-input" value="Assigned to you" disabled readOnly />
            <small className="ty-muted">
              Personal accounts keep every complaint assigned to you.
            </small>
          </div>
        )}

        {mode === 'edit' && can.canChangeStatus && (
          <div className="ty-field">
            <label>Status</label>
            <select
              className="ty-select"
              value={form.status || 'pending'}
              onChange={(e) => set('status', e.target.value)}
            >
              {COMPLAINT_STATUS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      )}

      {/* The complainant IS the logged-in external user — their name,
          email and mobile already came across in the signed SSO payload,
          so asking them to retype it (and letting them type someone
          else's) would be both redundant and a spoofing vector. */}
      {!can.isExternal && (
      <>
      <h4 className="ty-mt-3">Complainant details (optional — for public complaints)</h4>
      <div className="ty-grid-2">
        <div className="ty-field">
          <label>Full name</label>

          <input
            className="ty-input"
            value={form.fullname || ''}
            onChange={(e) => set('fullname', e.target.value)}
          />
        </div>
        <div className="ty-field">
          <label>Mobile</label>
          <input
            className="ty-input"
            inputMode="tel"
            value={form.mobile_number || ''}
            onChange={(e) => set('mobile_number', e.target.value)}
          />
        </div>
        <div className="ty-field">
          <label>Email</label>
          <input
            className="ty-input"
            type="email"
            value={form.email || ''}
            onChange={(e) => set('email', e.target.value)}
          />
        </div>
        <div className="ty-field">
          <label>Registration #</label>
          <input
            className="ty-input"
            value={form.registeration_number || ''}
            onChange={(e) => set('registeration_number', e.target.value)}
          />
        </div>
      </div>
      </>
      )}

      <div className="ty-field">
        <label>Attachment</label>

        <input
          className="ty-input"
          type="file"
          onChange={(e) => set('file', e.target.files[0] || null)}
        />
      </div>

      <div className="ty-row ty-row--end">
        <button className="ty-btn" type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save complaint'}
        </button>
      </div>
    </form>
  );
}
